// src/components/profile/components/VideoUpload.jsx
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Typography, Spinner, IconButton } from '@material-tailwind/react';
import {
  XMarkIcon,
  TrashIcon,
  VideoCameraIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { uploadVideoWithThumbnail } from '../../../services/storage';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts/AuthContext';

export const VideoUpload = ({
  value,
  onChange,
  label = "Upload Video",
  maxSize = 50, // MB
  disabled = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [isPlayerOpen, setPlayerOpen] = useState(false);
  const { jeweller } = useAuth();

  const handleFile = async (file) => {
    setError('');
    if (!file.type.startsWith('video/')) {
      setError('Please upload a valid video file (e.g., MP4, MOV).');
      return;
    }
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB.`);
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadProgress('Generating thumbnail...');
      
      // Upload video and generate thumbnail
      const { videoUrl, thumbnailUrl } = await uploadVideoWithThumbnail(
        file,
        jeweller.id
      );
      
      setUploadProgress('Saving...');
      
      // Update Firestore with both URLs
      const jewellerRef = doc(db, 'jewellers', jeweller.id);
      await updateDoc(jewellerRef, {
        showcaseVideo: videoUrl,
        video: {
          thumbnailUrl: thumbnailUrl,
          uploadedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      onChange(videoUrl);
      setUploadProgress('');
      
    } catch (err) {
      setError('Failed to upload video. Please try again.');
      console.error('Video upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = () => {
    onChange('');
    setError('');
  };

  // Separate component for the video player that uses portal
  const VideoPlayer = ({ videoUrl, isOpen, onClose }) => {
    if (!isOpen || !videoUrl) return null;
    
    return createPortal(
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" 
        style={{ 
          zIndex: 'var(--z-modal, 9999)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
        onClick={onClose}
      >
        <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
          <video 
            src={videoUrl} 
            controls 
            autoPlay 
            className="w-full h-auto max-h-[90vh] rounded-lg" 
          />
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2">
            <IconButton 
              className="bg-white/80 hover:bg-white rounded-full shadow-lg" 
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6 text-gray-800" />
            </IconButton>
          </div>
        </div>
      </div>,
      document.body // Render to body instead of within the component tree
    );
  };

  return (
    <>
      <div className="w-full max-w-md">
        {value ? (
          <div className="relative group aspect-video rounded-lg overflow-hidden shadow-md bg-black">
            <video src={value} className="w-full h-full object-cover" muted playsInline />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 flex items-center justify-center gap-4 transition-all duration-300">
              <IconButton 
                className="bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100" 
                onClick={() => setPlayerOpen(true)}
              >
                <PlayIcon className="h-8 w-8 text-gray-800" />
              </IconButton>
              <IconButton 
                className="bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100" 
                onClick={handleRemove}
              >
                <TrashIcon className="h-6 w-6 text-red-500" />
              </IconButton>
            </div>
          </div>
        ) : (
          <div
            className={`
              relative w-full aspect-video rounded-lg border-2 border-dashed
              flex flex-col items-center justify-center transition-colors
              ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
              ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
            `}
            onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
            onDragEnter={handleDrag} 
            onDragLeave={handleDrag} 
            onDragOver={handleDrag} 
            onDrop={handleDrop}
          >
            {isUploading ? (
              <>
                <Spinner className="h-8 w-8 mb-2" />
                <Typography>{uploadProgress || 'Uploading video...'}</Typography>
              </>
            ) : (
              <>
                <VideoCameraIcon className="h-12 w-12 text-gray-400" />
                <Typography className="mt-2 text-gray-600">Click or drag file to upload</Typography>
                <Typography variant="small" className="text-gray-500 mt-1">Max {maxSize}MB</Typography>
              </>
            )}
          </div>
        )}
        
        <input 
          ref={fileInputRef} 
          type="file" 
          className="hidden" 
          accept="video/*" 
          onChange={handleFileChange} 
          disabled={disabled || isUploading} 
        />
        
        {error && (
          <Typography variant="small" className="text-red-500 mt-2">
            {error}
          </Typography>
        )}
      </div>
      
      {/* Render video player using portal */}
      <VideoPlayer 
        videoUrl={value}
        isOpen={isPlayerOpen}
        onClose={() => setPlayerOpen(false)}
      />
    </>
  );
};